import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

const Error404 = ({ error: suppliedError }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Errors can be supplied by a parent or through navigation state.
  const error = suppliedError || location.state?.error;

  const message =
    (typeof error === "string" ? error : error?.message) ||
    "This page could not be displayed. No detailed error was supplied.";

  const status = error?.status || error?.statusCode;
  const stack = typeof error?.stack === "string" ? error.stack : "";
  const currentPath = location.pathname + location.search + location.hash;

  return (
    <main style={styles.page}>
      <section style={styles.card} aria-labelledby="error-title">
        <h1 id="error-title" style={styles.title}>
          {status ? `Error ${status}` : "Page unavailable"}
        </h1>

        <p role="alert" style={styles.message}>
          {message}
        </p>

        <div style={styles.details}>
          <strong>Requested page</strong>
          <code style={styles.path}>{currentPath}</code>

          {error?.name && (
            <p>
              <strong>Error type:</strong> {error.name}
            </p>
          )}

          {!error && (
            <>
              <p>Check these possible causes:</p>
              <ul style={styles.list}>
                <li>The URL does not match a configured route.</li>
                <li>The module’s table configuration is missing.</li>
                <li>
                  A component sent you here without passing its error details.
                </li>
              </ul>
              <p>
                Check the browser Console and Network tabs for the underlying
                error.
              </p>
            </>
          )}
        </div>

        {stack && (
          <details style={styles.stackSection}>
            <summary style={{ cursor: "pointer" }}>
              Show technical error details
            </summary>
            <pre style={styles.stack}>{stack}</pre>
          </details>
        )}

        <div style={styles.actions}>
          <button
            type="button"
            style={styles.primaryButton}
            onClick={() => window.location.reload()}
          >
            Retry page
          </button>

          <button
            type="button"
            style={styles.button}
            onClick={() => navigate("/", { replace: true })}
          >
            Go to home
          </button>

          <button
            type="button"
            style={styles.button}
            onClick={() => navigate("/login", { replace: true })}
          >
            Go to login
          </button>
        </div>
      </section>
    </main>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    padding: 24,
    boxSizing: "border-box",
    background: "#f4f6f8",
    color: "#172033",
  },
  card: {
    width: "100%",
    maxWidth: 680,
    minWidth: 0,
    padding: 28,
    boxSizing: "border-box",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
  },
  title: {
    margin: "0 0 16px",
    fontSize: 26,
  },
  message: {
    color: "#b42318",
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },
  details: {
    marginTop: 20,
    padding: 16,
    borderRadius: 8,
    background: "#f8fafc",
    lineHeight: 1.6,
  },
  path: {
    display: "block",
    marginTop: 8,
    overflowWrap: "anywhere",
  },
  list: {
    paddingLeft: 22,
  },
  stackSection: {
    marginTop: 20,
  },
  stack: {
    padding: 14,
    borderRadius: 8,
    background: "#111827",
    color: "#e5e7eb",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    maxHeight: 300,
    overflow: "auto",
    fontSize: 12,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 24,
  },
  button: {
    padding: "10px 16px",
    borderRadius: 6,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#172033",
    cursor: "pointer",
    font: "inherit",
  },
  primaryButton: {
    padding: "10px 16px",
    borderRadius: 6,
    border: "1px solid #2563eb",
    background: "#2563eb",
    color: "#ffffff",
    cursor: "pointer",
    font: "inherit",
  },
};

export default Error404;
